
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

export default function Contact() {
  const { t } = useTranslation('common');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const response = await fetch('https://readdy.ai/api/public/form/submit/js-glass-contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formData as Record<string, string>)
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', phone: '', message: '' });
        setTimeout(() => setSubmitStatus('idle'), 5000);
      } else {
        setSubmitStatus('error');
        setTimeout(() => setSubmitStatus('idle'), 5000);
      }
    } catch {
      setSubmitStatus('error');
      setTimeout(() => setSubmitStatus('idle'), 5000);
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactItems = [
    {
      icon: 'ri-map-pin-line',
      label: t('contact.address'),
      value: t('contact.addressValue'),
      href: undefined
    },
    {
      icon: 'ri-phone-line',
      label: t('contact.phone'),
      value: t('contact.phoneValue'),
      href: 'tel:+16264432688'
    },
    {
      icon: 'ri-mail-line',
      label: t('contact.email'),
      value: t('contact.emailValue'),
      href: 'mailto:info@jsglass.com'
    },
    {
      icon: 'ri-time-line',
      label: t('contact.hours'),
      value: t('contact.hoursValue'),
      extra: t('contact.hoursClosed'),
      href: undefined
    }
  ];

  return (
    <section className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-16">
          <p className="text-amber-700 text-xs font-semibold tracking-[0.2em] mb-4 uppercase">{t('contact.badge')}</p>
          <h2 className="text-3xl md:text-4xl font-light text-stone-800 mb-4">
            {t('contact.title')}
          </h2>
          <div className="w-12 h-px bg-amber-400 mx-auto mb-6"></div>
          <p className="text-stone-500 text-sm max-w-2xl mx-auto leading-relaxed font-light">
            {t('contact.subtitle')}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-10">
          {/* Contact Form - 3 cols */}
          <div className="lg:col-span-3 bg-amber-50/40 rounded-2xl p-8 md:p-10">
            <h3 className="text-xl font-medium text-stone-800 mb-1">{t('contact.formTitle')}</h3>
            <p className="text-stone-400 text-xs mb-8">{t('contact.formSubtitle')}</p>

            <form id="contact-form" data-readdy-form onSubmit={handleSubmit} className="space-y-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label htmlFor="name" className="block text-xs font-medium text-stone-500 mb-2 tracking-wider uppercase">
                    {t('contact.name')}
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    className="w-full px-4 py-3 rounded-lg border border-stone-200 bg-white focus:ring-2 focus:ring-amber-400 focus:border-transparent transition-all text-sm"
                  />
                </div>
                <div>
                  <label htmlFor="phone" className="block text-xs font-medium text-stone-500 mb-2 tracking-wider uppercase">
                    {t('contact.phone')}
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    required
                    className="w-full px-4 py-3 rounded-lg border border-stone-200 bg-white focus:ring-2 focus:ring-amber-400 focus:border-transparent transition-all text-sm"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="email" className="block text-xs font-medium text-stone-500 mb-2 tracking-wider uppercase">
                  {t('contact.email')}
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  className="w-full px-4 py-3 rounded-lg border border-stone-200 bg-white focus:ring-2 focus:ring-amber-400 focus:border-transparent transition-all text-sm"
                />
              </div>

              <div>
                <label htmlFor="message" className="block text-xs font-medium text-stone-500 mb-2 tracking-wider uppercase">
                  {t('contact.message')}
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={(e) => {
                    if (e.target.value.length <= 500) {
                      setFormData({ ...formData, message: e.target.value });
                    }
                  }}
                  required
                  rows={4}
                  maxLength={500}
                  className="w-full px-4 py-3 rounded-lg border border-stone-200 bg-white focus:ring-2 focus:ring-amber-400 focus:border-transparent transition-all text-sm resize-none"
                ></textarea>
                <p className="text-[10px] text-stone-400 mt-1">{formData.message.length}/500</p>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-stone-800 text-white py-3.5 rounded-lg text-sm font-medium hover:bg-stone-700 transition-all duration-300 disabled:bg-stone-300 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
              >
                {isSubmitting ? t('contact.sending') : t('contact.send')}
              </button>

              {submitStatus === 'success' && (
                <div className="bg-amber-50 text-amber-800 px-4 py-3 rounded-lg text-xs flex items-center gap-2">
                  <i className="ri-check-line text-base"></i>
                  {t('contact.success')}
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="bg-red-50 text-red-600 px-4 py-3 rounded-lg text-xs flex items-center gap-2">
                  <i className="ri-error-warning-line text-base"></i>
                  {t('contact.error')}
                </div>
              )}
            </form>
          </div>

          {/* Contact Info - 2 cols */}
          <div className="lg:col-span-2 space-y-6">
            {/* Info Cards */}
            <div className="space-y-4">
              {contactItems.map((item, index) => (
                <div key={index} className="flex items-start gap-4 p-4 rounded-xl bg-amber-50/40 hover:bg-amber-50/70 transition-colors">
                  <div className="w-10 h-10 flex items-center justify-center bg-amber-100/60 rounded-lg flex-shrink-0">
                    <i className={`${item.icon} text-amber-700 text-lg`}></i>
                  </div>
                  <div>
                    <h4 className="text-xs font-semibold text-stone-800 mb-0.5 tracking-wider uppercase">{item.label}</h4>
                    {item.href ? (
                      <a href={item.href} className="text-stone-500 text-sm hover:text-amber-700 transition-colors cursor-pointer">
                        {item.value}
                      </a>
                    ) : (
                      <>
                        <p className="text-stone-500 text-sm">{item.value}</p>
                        {item.extra && <p className="text-stone-400 text-xs mt-0.5">{item.extra}</p>}
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Map */}
            <div className="rounded-xl overflow-hidden" style={{ width: '100%', height: '280px' }}>
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3302.8234567890123!2d-118.0276543!3d34.0689012!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x80c2d1234567890a%3A0x1234567890abcdef!2s12211%20Garvey%20Ave%2C%20El%20Monte%2C%20CA%2091732!5e0!3m2!1sen!2sus!4v1234567890123!5m2!1sen!2sus"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="JS Glass Corporation Location"
              ></iframe>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
